// Zoho CRM Service
// This service handles all interactions with Zoho CRM API

import { ZOHO_CONFIG, DEV_CONFIG, isZohoConfigured, getZohoHeaders } from '../config/zohoConfig';

class ZohoCRMService {
  constructor() {
    this.baseUrl = ZOHO_CONFIG.BASE_URL;
    this.accessToken = ZOHO_CONFIG.ACCESS_TOKEN;
    this.orgId = ZOHO_CONFIG.ORG_ID;
  }

  // Check if a mobile number already exists in Zoho CRM
  async checkMobileExists(mobile) {
    try {
      // TODO: Replace with actual Zoho CRM API call
      // This should search for leads/contacts with the given mobile number
      
      const response = await fetch(`${this.baseUrl}${ZOHO_CONFIG.ENDPOINTS.SEARCH}?mobile=${mobile}`, {
        method: 'GET',
        headers: getZohoHeaders()
      });

      if (!response.ok) {
        throw new Error(`Zoho CRM API error: ${response.status}`);
      }

      const data = await response.json();
      
      // Check if any leads were found
      return {
        exists: data.data && data.data.length > 0,
        message: data.data && data.data.length > 0 
          ? 'Mobile number already registered' 
          : 'Mobile number is new',
        data: data.data || []
      };
    } catch (error) {
      console.error('Zoho CRM check error:', error);
      
      // For development/testing, simulate the API call
      if (process.env.NODE_ENV === 'development') {
        return this.simulateCheckMobile(mobile);
      }
      
      throw new Error('Failed to check mobile number in CRM');
    }
  }

  // Create a new lead in Zoho CRM
  async createLead(userData) {
    try {
      const leadData = {
        data: [{
          Last_Name: userData.lastName,
          First_Name: userData.firstName,
          Email: userData.email,
          Mobile: userData.mobile,
          Lead_Source: ZOHO_CONFIG.LEAD_SOURCES.WEBSITE,
          Lead_Status: ZOHO_CONFIG.LEAD_STATUSES.NEW,
          Company: 'Individual',
          Description: 'New admission application registration'
        }]
      };

      const response = await fetch(`${this.baseUrl}${ZOHO_CONFIG.ENDPOINTS.LEADS}`, {
        method: 'POST',
        headers: getZohoHeaders(),
        body: JSON.stringify(leadData)
      });

      if (!response.ok) {
        throw new Error(`Zoho CRM API error: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        success: true,
        leadId: data.data[0].details.id,
        message: 'Lead created successfully in CRM'
      };
    } catch (error) {
      console.error('Zoho CRM create lead error:', error);
      
      // For development/testing, simulate the API call
      if (process.env.NODE_ENV === 'development') {
        return this.simulateCreateLead(userData);
      }
      
      throw new Error('Failed to create lead in CRM');
    }
  }

  // Update lead status (e.g., when application progresses)
  async updateLeadStatus(leadId, status, additionalData = {}) {
    try {
      const updateData = {
        data: [{
          id: leadId,
          Lead_Status: status,
          ...additionalData
        }]
      };

      const response = await fetch(`${this.baseUrl}${ZOHO_CONFIG.ENDPOINTS.LEADS}`, {
        method: 'PUT',
        headers: getZohoHeaders(),
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error(`Zoho CRM API error: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        success: true,
        message: 'Lead updated successfully in CRM'
      };
    } catch (error) {
      console.error('Zoho CRM update lead error:', error);
      throw new Error('Failed to update lead in CRM');
    }
  }

  // Get lead details by ID
  async getLeadDetails(leadId) {
    try {
      const response = await fetch(`${this.baseUrl}${ZOHO_CONFIG.ENDPOINTS.LEADS}/${leadId}`, {
        method: 'GET',
        headers: getZohoHeaders()
      });

      if (!response.ok) {
        throw new Error(`Zoho CRM API error: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        success: true,
        lead: data.data[0]
      };
    } catch (error) {
      console.error('Zoho CRM get lead error:', error);
      throw new Error('Failed to get lead details from CRM');
    }
  }

  // Development/testing simulation methods
  simulateCheckMobile(mobile) {
    // Simulate API delay
    return new Promise((resolve) => {
      setTimeout(() => {
        // For testing: simulate that 1234567890 already exists
        if (mobile === '1234567890') {
          resolve({
            exists: true,
            message: 'Mobile number already registered',
            data: [{
              id: 'LEAD_12345',
              First_Name: 'Test',
              Last_Name: 'User',
              Email: 'test@example.com',
              Mobile: mobile,
              Lead_Status: ZOHO_CONFIG.LEAD_STATUSES.QUALIFIED
            }]
          });
        } else {
          resolve({
            exists: false,
            message: 'Mobile number is new',
            data: []
          });
        }
      }, DEV_CONFIG.SIMULATE_DELAY);
    });
  }

  simulateCreateLead(userData) {
    // Simulate API delay
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          leadId: 'LEAD_' + Date.now(),
          message: 'Lead created successfully in CRM (Simulated)'
        });
      }, DEV_CONFIG.SIMULATE_DELAY);
    });
  }
}

export default new ZohoCRMService();
